import React from 'react';

import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { buildDatePresetRange } from '~/services/TimeService';

import { COLOR_WHITE, CS_FEATURES, CS_FEATURE_MAP } from './features/registry';

type Props = {
  selectedFeatureIds: string[];
  selectedTeamLeader: string;
  startAt: string;
  endAt: string;
  teamLeaders: Array<{ email: string; name: string }>;
  updateUrlParams: (updates: Record<string, string>) => void;
};

export default function CsDashboardFilters({
  selectedFeatureIds,
  selectedTeamLeader,
  startAt,
  endAt,
  teamLeaders,
  updateUrlParams,
}: Props) {
  function applyDatePreset(amount: number, unit: 'day' | 'month') {
    const { startAt, endAt } = buildDatePresetRange(amount, unit);
    updateUrlParams({ start_at: startAt, end_at: endAt });
  }

  return (
    <Box mt={4}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Filters
        </Typography>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
          <FormControl sx={{ minWidth: 260 }} size="small">
            <InputLabel id="features-label">Features</InputLabel>
            <Select
              labelId="features-label"
              multiple
              value={selectedFeatureIds}
              onChange={(e) => {
                const value = e.target.value;
                const nextSelectedFeatureIds = typeof value === 'string' ? value.split(',') : value;
                updateUrlParams({ features: nextSelectedFeatureIds.join(',') });
              }}
              input={<OutlinedInput label="Features" />}
              renderValue={(selected) => (
                <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ rowGap: 0.5 }}>
                  {(selected as string[]).map((id) => {
                    const feature = CS_FEATURE_MAP[id];
                    return feature ? (
                      <Chip
                        key={id}
                        label={feature.label}
                        size="small"
                        sx={{ bgcolor: feature.color, color: COLOR_WHITE, height: 22 }}
                      />
                    ) : null;
                  })}
                </Stack>
              )}
            >
              {CS_FEATURES.map((feature) => (
                <MenuItem key={feature.id} value={feature.id}>
                  <Checkbox checked={selectedFeatureIds.includes(feature.id)} size="small" />
                  <ListItemText primary={feature.label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 220 }} size="small">
            <InputLabel id="team-leader-label">Filter by Team Leader</InputLabel>
            <Select
              labelId="team-leader-label"
              value={selectedTeamLeader}
              label="Filter by Team Leader"
              onChange={(e) => updateUrlParams({ teamLeader: e.target.value })}
            >
              <MenuItem value="">
                <Typography fontStyle="italic">All Agents</Typography>
              </MenuItem>
              {teamLeaders.map((leader) => (
                <MenuItem key={leader.email} value={leader.email}>
                  {leader.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Start date"
            type="date"
            size="small"
            value={startAt}
            onChange={(e) => updateUrlParams({ start_at: e.target.value })}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 160 }}
          />
          <TextField
            label="End date"
            type="date"
            size="small"
            value={endAt}
            onChange={(e) => updateUrlParams({ end_at: e.target.value })}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 160 }}
          />

          <Button variant="outlined" size="small" onClick={() => applyDatePreset(14, 'day')}>
            Last 2 weeks
          </Button>
          <Button variant="outlined" size="small" onClick={() => applyDatePreset(28, 'day')}>
            Last 4 weeks
          </Button>
          <Button variant="outlined" size="small" onClick={() => applyDatePreset(3, 'month')}>
            Last 3 months
          </Button>

          {(startAt || endAt || selectedTeamLeader) && (
            <Button variant="text" onClick={() => updateUrlParams({ start_at: '', end_at: '', teamLeader: '' })}>
              Clear filters
            </Button>
          )}
        </Stack>
      </Paper>
    </Box>
  );
}
